import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Checkbox,
  TextField,
  Autocomplete,
  Grid,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
} from '@mui/material';
import {
  SwapVertOutlined,
  SearchOutlined,
  FileDownloadOutlined,
  PictureAsPdfOutlined,
  TableChartOutlined,
  KeyboardArrowDownOutlined,
  CheckBoxOutlineBlankOutlined,
  CheckBoxOutlined,
} from '@mui/icons-material';
import { api } from '../../services/api';
import { Item, Partner } from '../../types/api';
import { INDIA_STATES } from '../../constants/indiaLocations';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

interface Category {
  id: number;
  name: string;
}

interface InventoryMovementEntry {
  id: number;
  date: string;
  order_id: number;
  partner_name: string;
  item_name: string;
  quantity: number;
  movement_type: 'INWARD' | 'OUTWARD' | 'RETURN';
}

interface MovementSummary {
  total_inward: number;
  total_outward: number;
  total_return: number;
}

interface PaginatedResponse {
  data: InventoryMovementEntry[];
  limit: number;
  next_id?: number;
}

const checkboxIcon = <CheckBoxOutlineBlankOutlined fontSize="small" />;
const checkboxCheckedIcon = <CheckBoxOutlined fontSize="small" />;

const InventoryMovementV2: React.FC = () => {
  // State for filters — category/item/partner/state now accept multiple selections, matching the
  // backend's comma-separated list support (direction remains single-value, backend didn't change that).
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [selectedItems, setSelectedItems] = useState<Item[]>([]);
  const [selectedPartners, setSelectedPartners] = useState<Partner[]>([]);
  const [selectedDirection, setSelectedDirection] = useState<string | null>(null);
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<string>(() => {
    const today = new Date();
    return today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
  });
  const [endDate, setEndDate] = useState<string>(() => {
    const today = new Date();
    return today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
  });

  // State for dropdown options
  const [categories, setCategories] = useState<Category[]>([]);
  const [allItems, setAllItems] = useState<any[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingPartners, setLoadingPartners] = useState(true);

  // State for results
  const [summary, setSummary] = useState<MovementSummary | null>(null);
  const [movements, setMovements] = useState<InventoryMovementEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextId, setNextId] = useState<number | undefined>(undefined);

  // Export menu state
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);
  const [exporting, setExporting] = useState(false);
  const exportMenuOpen = Boolean(exportAnchorEl);

  // Direction options
  const directionOptions = [
    { label: 'Inward', value: 'INWARD' },
    { label: 'Outward', value: 'OUTWARD' },
    { label: 'Return', value: 'RETURN' },
  ];

  // Fetch categories for dropdown
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get<{ categories: Category[] }>('/api/categories');
        setCategories(response.data.categories || []);
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, []);

  // Fetch all items once on page load
  useEffect(() => {
    api
      .get('/api/items')
      .then((res) => {
        const items = res.data.items || [];
        setAllItems(
          items.map((item: any) => ({
            id: item.id,
            name: item.name,
            category_id: item.category_id,
            category_name: item.category_name,
          }))
        );
      })
      .catch((err) => console.error('Error fetching items:', err));
  }, []);

  // Filter items by selected categories
  const filteredItems =
    selectedCategories.length > 0
      ? allItems.filter((item) => selectedCategories.some((c) => c.id === item.category_id))
      : allItems;

  // Reset selected items when the selected categories change
  useEffect(() => {
    setSelectedItems([]);
  }, [selectedCategories]);

  // Fetch partners for dropdown
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await api.get<{ partners: Partner[] }>('/api/partners');
        setPartners(response.data.partners || []);
      } catch (error) {
        console.error('Error fetching partners:', error);
      } finally {
        setLoadingPartners(false);
      }
    };
    fetchPartners();
  }, []);

  // Fetch summary
  const fetchSummary = useCallback(async () => {
    if (selectedCategories.length === 0) return;

    try {
      const params = new URLSearchParams({
        category_id: selectedCategories.map((c) => c.id).join(','),
        start_date: startDate,
        end_date: endDate,
      });

      if (selectedItems.length > 0) params.append('item_id', selectedItems.map((i) => i.id).join(','));
      if (selectedPartners.length > 0) params.append('partner_id', selectedPartners.map((p) => p.id).join(','));
      if (selectedDirection) params.append('direction', selectedDirection);
      if (selectedStates.length > 0) params.append('state', selectedStates.join(','));

      const response = await api.get<{ summary: MovementSummary }>(`/api/stock-movements/inventory/summary?${params}`);
      setSummary(response.data.summary || null);
    } catch (error) {
      console.error('Error fetching summary:', error);
      setSummary(null);
    }
  }, [selectedCategories, selectedItems, selectedPartners, selectedDirection, selectedStates, startDate, endDate]);

  // Fetch inventory movements
  const fetchMovements = useCallback(
    async (beforeId?: number) => {
      if (selectedCategories.length === 0) return;

      const isLoadingMore = beforeId !== undefined;
      if (isLoadingMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        const params = new URLSearchParams({
          category_id: selectedCategories.map((c) => c.id).join(','),
          start_date: startDate,
          end_date: endDate,
          limit: '15',
        });

        if (selectedItems.length > 0) params.append('item_id', selectedItems.map((i) => i.id).join(','));
        if (selectedPartners.length > 0) params.append('partner_id', selectedPartners.map((p) => p.id).join(','));
        if (selectedDirection) params.append('direction', selectedDirection);
        if (selectedStates.length > 0) params.append('state', selectedStates.join(','));
        if (beforeId) params.append('before_id', beforeId.toString());

        const response = await api.get<PaginatedResponse>(`/api/stock-movements/inventory/paginated?${params}`);
        const newMovements = response.data.data || [];

        if (isLoadingMore) {
          setMovements((prev) => [...prev, ...newMovements]);
        } else {
          setMovements(newMovements);
        }

        // Check if there's more data
        const hasNext = response.data.next_id !== undefined && newMovements.length > 0;
        setHasMoreData(hasNext);
        setNextId(response.data.next_id);
      } catch (error) {
        console.error('Error fetching inventory movements:', error);
        if (!isLoadingMore) {
          setMovements([]);
        }
        setHasMoreData(false);
      } finally {
        if (isLoadingMore) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [selectedCategories, selectedItems, selectedPartners, selectedDirection, selectedStates, startDate, endDate]
  );

  // Handle search button click
  const handleSearch = () => {
    setMovements([]);
    setHasMoreData(false);
    setNextId(undefined);
    setHasSearched(true);
    Promise.all([fetchSummary(), fetchMovements()]);
  };

  // Handle load more
  const handleLoadMore = () => {
    if (nextId !== undefined) {
      fetchMovements(nextId);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getMovementTypeChip = (type: string) => {
    const chipProps: { label: string; color: 'success' | 'error' | 'warning' } = {
      label: type,
      color: 'warning',
    };

    switch (type) {
      case 'INWARD':
        chipProps.color = 'success';
        break;
      case 'OUTWARD':
        chipProps.color = 'error';
        break;
      case 'RETURN':
        chipProps.color = 'warning';
        break;
    }

    return <Chip label={chipProps.label} color={chipProps.color} size="small" />;
  };

  // Export functions
  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  const fetchAllForExport = async (): Promise<InventoryMovementEntry[]> => {
    if (selectedCategories.length === 0) return [];

    try {
      const allMovements: InventoryMovementEntry[] = [];
      let currentBeforeId: number | undefined = undefined;

      // Fetch all pages
      while (true) {
        const params = new URLSearchParams({
          category_id: selectedCategories.map((c) => c.id).join(','),
          start_date: startDate,
          end_date: endDate,
          limit: '500', // Use max limit for export
        });

        if (selectedItems.length > 0) params.append('item_id', selectedItems.map((i) => i.id).join(','));
        if (selectedPartners.length > 0) params.append('partner_id', selectedPartners.map((p) => p.id).join(','));
        if (selectedDirection) params.append('direction', selectedDirection);
        if (selectedStates.length > 0) params.append('state', selectedStates.join(','));
        if (currentBeforeId) params.append('before_id', currentBeforeId.toString());

        const response = await api.get<PaginatedResponse>(`/api/stock-movements/inventory/paginated?${params}`);
        const pageData = response.data.data || [];
        allMovements.push(...pageData);

        // If no next_id or no data, we've reached the end
        if (!response.data.next_id || pageData.length === 0) {
          break;
        }

        currentBeforeId = response.data.next_id;
      }

      return allMovements;
    } catch (error) {
      console.error('Error fetching data for export:', error);
      return [];
    }
  };

  const formatDateForExport = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const exportToPDF = async () => {
    handleExportClose();
    setExporting(true);

    try {
      const data = await fetchAllForExport();
      if (data.length === 0) {
        alert('No data to export');
        return;
      }

      const doc = new jsPDF();

      // Title
      doc.setFontSize(11);
      doc.text('Inventory Movement Report', 14, 8);

      // Subtitle with filters
      doc.setFontSize(7);
      doc.text(`Category: ${selectedCategories.map((c) => c.name).join(', ') || '-'}`, 14, 13);
      doc.text(`Item: ${selectedItems.map((i) => i.name).join(', ') || 'All'}`, 14, 17);
      doc.text(`Partner: ${selectedPartners.map((p) => p.name).join(', ') || 'All'}`, 14, 21);
      doc.text(`Direction: ${selectedDirection || 'All'}`, 14, 25);
      doc.text(`State: ${selectedStates.join(', ') || 'All'}`, 14, 29);
      doc.text(`Period: ${startDate} to ${endDate}`, 14, 33);

      // Summary section
      let currentY = 37;
      doc.setFontSize(8);
      doc.text('Summary', 14, currentY);
      currentY += 4;
      doc.setFontSize(7);
      doc.text(`Total Inward: ${summary?.total_inward || 0}`, 14, currentY);
      currentY += 3;
      doc.text(`Total Outward: ${summary?.total_outward || 0}`, 14, currentY);
      currentY += 3;
      doc.text(`Total Return: ${summary?.total_return || 0}`, 14, currentY);
      currentY += 5;

      // Table
      const tableData = data.map((movement) => [
        formatDateForExport(movement.date),
        `#${movement.order_id}`,
        movement.partner_name,
        movement.item_name,
        movement.quantity.toString(),
        movement.movement_type,
      ]);

      autoTable(doc, {
        startY: currentY,
        head: [['Date', 'Order ID', 'Partner Name', 'Item Name', 'Quantity', 'Movement Type']],
        body: tableData,
        styles: {
          fontSize: 6,
          cellPadding: 1,
          lineColor: [200, 200, 200],
          lineWidth: 0.1,
        },
        headStyles: {
          fontSize: 6,
          fontStyle: 'bold',
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
        },
        tableLineColor: [200, 200, 200],
        tableLineWidth: 0.1,
      });

      const categoryLabel = selectedCategories.map((c) => c.name).join('-') || 'all';
      doc.save(`inventory-movement-${categoryLabel}-${startDate}-to-${endDate}.pdf`);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      alert('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const exportToExcel = async () => {
    handleExportClose();
    setExporting(true);

    try {
      const data = await fetchAllForExport();
      if (data.length === 0) {
        alert('No data to export');
        return;
      }

      const worksheetData = [
        ['Inventory Movement Report'],
        [`Category: ${selectedCategories.map((c) => c.name).join(', ') || '-'}`],
        [`Item: ${selectedItems.map((i) => i.name).join(', ') || 'All'}`],
        [`Partner: ${selectedPartners.map((p) => p.name).join(', ') || 'All'}`],
        [`Direction: ${selectedDirection || 'All'}`],
        [`State: ${selectedStates.join(', ') || 'All'}`],
        [`Period: ${startDate} to ${endDate}`],
        [],
        ['Summary'],
        [`Total Inward: ${summary?.total_inward || 0}`],
        [`Total Outward: ${summary?.total_outward || 0}`],
        [`Total Return: ${summary?.total_return || 0}`],
        [],
        ['Date', 'Order ID', 'Partner Name', 'Quantity', 'Movement Type'],
        ...data.map((movement) => [
          formatDateForExport(movement.date),
          movement.order_id,
          movement.partner_name,
          movement.item_name,
          movement.quantity,
          movement.movement_type,
        ]),
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Inventory Movement');

      // Set column widths
      worksheet['!cols'] = [
        { wch: 20 }, // Date
        { wch: 12 }, // Order ID
        { wch: 25 }, // Partner Name
        { wch: 10 }, // Quantity
        { wch: 15 }, // Movement Type
      ];

      const categoryLabel = selectedCategories.map((c) => c.name).join('-') || 'all';
      XLSX.writeFile(workbook, `inventory-movement-${categoryLabel}-${startDate}-to-${endDate}.xlsx`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Failed to export Excel');
    } finally {
      setExporting(false);
    }
  };

  const canSearch = selectedCategories.length > 0 && startDate && endDate;

  return (
    <Paper sx={{ p: 2, m: 0 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SwapVertOutlined sx={{ fontSize: 28, mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h6" fontWeight="bold">
            Inventory Movement Report (V2)
          </Typography>
        </Box>
        {hasSearched && !loading && movements.length > 0 && (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={exporting ? <CircularProgress size={16} /> : <FileDownloadOutlined />}
              onClick={handleExportClick}
              disabled={exporting}
            >
              Export
            </Button>
            <Menu
              anchorEl={exportAnchorEl}
              open={exportMenuOpen}
              onClose={handleExportClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={exportToPDF}>
                <ListItemIcon>
                  <PictureAsPdfOutlined fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export as PDF</ListItemText>
              </MenuItem>
              <MenuItem onClick={exportToExcel}>
                <ListItemIcon>
                  <TableChartOutlined fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export as Excel</ListItemText>
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>

      {/* Description */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Track inventory movements for categories and items across a date range.
      </Typography>

      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            sx={{
              '& .MuiOutlinedInput-root': { flexWrap: 'nowrap' },
              '& .MuiAutocomplete-input': { flexGrow: 0, minWidth: '0 !important', width: '0 !important', padding: '0 !important' },
            }}
            options={categories}
            getOptionLabel={(option) => option.name}
            value={selectedCategories}
            onChange={(_event, newValue) => setSelectedCategories(newValue)}
            loading={loadingCategories}
            renderTags={(tagValue) =>
              tagValue.length === 0 ? null : (
                <Typography
                  component="span"
                  variant="body2"
                  noWrap
                  sx={{ ml: '14px', flexGrow: 1, minWidth: 0 }}
                >
                  {tagValue[0].name}
                  {tagValue.length > 1 ? ` +${tagValue.length - 1}` : ''}
                </Typography>
              )
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox icon={checkboxIcon} checkedIcon={checkboxCheckedIcon} checked={selected} sx={{ mr: 1 }} />
                {option.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Category *"
                placeholder={selectedCategories.length > 0 ? undefined : 'Select categories'}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCategories ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            sx={{
              '& .MuiOutlinedInput-root': { flexWrap: 'nowrap' },
              '& .MuiAutocomplete-input': { flexGrow: 0, minWidth: '0 !important', width: '0 !important', padding: '0 !important' },
            }}
            options={filteredItems}
            getOptionLabel={(option) => option.name}
            value={selectedItems}
            onChange={(_event, newValue) => setSelectedItems(newValue)}
            disabled={selectedCategories.length === 0}
            renderTags={(tagValue) =>
              tagValue.length === 0 ? null : (
                <Typography
                  component="span"
                  variant="body2"
                  noWrap
                  sx={{ ml: '14px', flexGrow: 1, minWidth: 0 }}
                >
                  {tagValue[0].name}
                  {tagValue.length > 1 ? ` +${tagValue.length - 1}` : ''}
                </Typography>
              )
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox icon={checkboxIcon} checkedIcon={checkboxCheckedIcon} checked={selected} sx={{ mr: 1 }} />
                {option.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Item"
                placeholder={selectedItems.length > 0 ? undefined : 'All items'}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            sx={{
              '& .MuiOutlinedInput-root': { flexWrap: 'nowrap' },
              '& .MuiAutocomplete-input': { flexGrow: 0, minWidth: '0 !important', width: '0 !important', padding: '0 !important' },
            }}
            options={partners}
            getOptionLabel={(option) => `${option.name} (${option.type})`}
            value={selectedPartners}
            onChange={(_event, newValue) => setSelectedPartners(newValue)}
            loading={loadingPartners}
            renderTags={(tagValue) =>
              tagValue.length === 0 ? null : (
                <Typography
                  component="span"
                  variant="body2"
                  noWrap
                  sx={{ ml: '14px', flexGrow: 1, minWidth: 0 }}
                >
                  {tagValue[0].name} ({tagValue[0].type})
                  {tagValue.length > 1 ? ` +${tagValue.length - 1}` : ''}
                </Typography>
              )
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox icon={checkboxIcon} checkedIcon={checkboxCheckedIcon} checked={selected} sx={{ mr: 1 }} />
                {option.name} ({option.type})
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Partner"
                placeholder={selectedPartners.length > 0 ? undefined : 'All partners'}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingPartners ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Autocomplete
            options={directionOptions}
            getOptionLabel={(option) => option.label}
            value={directionOptions.find((opt) => opt.value === selectedDirection) || null}
            onChange={(_event, newValue) => setSelectedDirection(newValue?.value || null)}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Direction" placeholder="All directions" />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            sx={{
              '& .MuiOutlinedInput-root': { flexWrap: 'nowrap' },
              '& .MuiAutocomplete-input': { flexGrow: 0, minWidth: '0 !important', width: '0 !important', padding: '0 !important' },
            }}
            options={INDIA_STATES}
            value={selectedStates}
            onChange={(_event, newValue) => setSelectedStates(newValue)}
            renderTags={(tagValue) =>
              tagValue.length === 0 ? null : (
                <Typography
                  component="span"
                  variant="body2"
                  noWrap
                  sx={{ ml: '14px', flexGrow: 1, minWidth: 0 }}
                >
                  {tagValue[0]}
                  {tagValue.length > 1 ? ` +${tagValue.length - 1}` : ''}
                </Typography>
              )
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox icon={checkboxIcon} checkedIcon={checkboxCheckedIcon} checked={selected} sx={{ mr: 1 }} />
                {option}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="State"
                placeholder={selectedStates.length > 0 ? undefined : 'All states'}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSearch}
            disabled={!canSearch || loading}
            startIcon={<SearchOutlined />}
            sx={{ height: '40px' }}
          >
            Search
          </Button>
        </Grid>
      </Grid>

      {/* Date Range */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <TextField
            size="small"
            fullWidth
            label="Start Date *"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <TextField
            size="small"
            fullWidth
            label="End Date *"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>

      {/* Summary Cards */}
      {loading && !hasSearched ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : hasSearched && summary ? (
        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card sx={{ bgcolor: '#e8f5e9', borderLeft: '4px solid #4caf50' }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    Total Inward:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" sx={{ color: '#2e7d32' }}>
                    {summary.total_inward}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card sx={{ bgcolor: '#ffebee', borderLeft: '4px solid #f44336' }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    Total Outward:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" sx={{ color: '#c62828' }}>
                    {summary.total_outward}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card sx={{ bgcolor: '#fff3e0', borderLeft: '4px solid #ff9800' }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    Total Return:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" sx={{ color: '#e65100' }}>
                    {summary.total_return}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : null}

      {/* Results */}
      {loading && hasSearched ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : hasSearched ? (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Order ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Partner Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Item Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">
                    Quantity
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">
                    Movement Type
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movements.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        No inventory movements found for the selected criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  movements.map((movement, index) => (
                    <TableRow key={`${movement.id}-${index}`} hover>
                      <TableCell>
                        <Typography variant="body2">{formatDate(movement.date)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium" sx={{ color: 'primary.main' }}>
                          #{movement.order_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{movement.partner_name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{movement.item_name}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {movement.quantity}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{getMovementTypeChip(movement.movement_type)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Load More Button */}
          {hasMoreData && movements.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleLoadMore}
                disabled={loadingMore}
                endIcon={loadingMore ? <CircularProgress size={16} /> : <KeyboardArrowDownOutlined />}
              >
                {loadingMore ? 'Loading...' : 'Load More'}
              </Button>
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Select at least one category and a date range, then click Search to view movements
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default InventoryMovementV2;
